import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { FileText, MessageSquare, Plus, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import { ErrorBanner } from "@/components/common/ErrorBanner";
import { PageHeader } from "@/components/common/FeatureShell";
import { markContentVisible } from "@/hooks/useNavPerf";
import { ProjectFileTree } from "@/components/projects/ProjectFileTree";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";

import { api, type ChatRow } from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { useSuspenseProject, useSuspenseProjectChats } from "@/hooks/queries/useProject";
import { cn } from "@/lib/utils";

type TabKey = "documents" | "assistant";

/**
 * Screen composition — Project
 *
 * Hero zone: file tree / document list (main body below tabs).
 * Scanning regions (compact): file-tree rows (`min-h-10`), chat list rows,
 *   tab bar.
 * Focus regions (airy): document viewer (inspector pane, full-height render),
 *   version history expansion.
 */
interface ProjectPageClientProps {
  projectId: string;
}

export function ProjectPageClient({ projectId }: ProjectPageClientProps) {
  const search = useSearch({ strict: false }) as { tab?: TabKey };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: project } = useSuspenseProject(projectId);
  const { data: chats } = useSuspenseProjectChats(projectId);
  const tab: TabKey = search.tab === "documents" ? "documents" : "assistant";

  const load = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: qk.project(projectId) }),
      queryClient.invalidateQueries({ queryKey: qk.projectChats(projectId) }),
    ]);
  }, [projectId, queryClient]);

  useEffect(() => {
    markContentVisible(!!project);
  }, [project]);

  const setTab = (next: TabKey) => void navigate({ to: "/projects/$id", params: { id: projectId }, search: { tab: next }, replace: true });

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow={project.cm_number ?? undefined}
        title={project.name}
        description={`${project.documents.length} document(s), ${chats.length} chat(s)`}
        actions={
          tab === "assistant" ? (
            <Button type="button" asChild><Link to="/projects/$id/assistant" params={{ id: projectId }}><Plus className="size-4" />New chat</Link></Button>
          ) : null
        }
      />
      <main className="p-4 md:p-6">
        <div className="mb-4 flex w-fit rounded-md border bg-card p-1">
          {([
            { key: "assistant", label: "Chats", icon: MessageSquare },
            { key: "documents", label: "Corpus", icon: FileText },
          ] as const).map((item) => (
            <button key={item.key} type="button" onClick={() => setTab(item.key)} className={cn("flex h-9 items-center gap-2 rounded px-3 text-sm", tab === item.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted")}>
              <item.icon className="size-4" />
              {item.label}
            </button>
          ))}
        </div>
        {tab === "documents" ? (
          <ProjectFileTree projectId={projectId} documents={project.documents} folders={project.folders} onRefresh={load} />
        ) : tab === "assistant" ? (
          <ProjectChats projectId={projectId} chats={chats} onRefresh={load} />
        ) : null}
      </main>
    </div>
  );
}

function ProjectChats({ projectId, chats, onRefresh }: { projectId: string; chats: ChatRow[]; onRefresh: () => Promise<void> }) {
  const { user } = useAuth();
  const [renaming, setRenaming] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [error, setError] = useState<string | null>(null);

  const run = async (action: () => Promise<unknown>, failMessage: string) => {
    setError(null);
    try {
      await action();
      await onRefresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : failMessage);
    }
  };

  const saveRename = (chatId: string) =>
    run(async () => {
      await api.chat.rename(chatId, title.trim() || "Untitled chat");
      setRenaming(null);
    }, "Could not rename the chat.");

  const toggleShare = (chat: ChatRow) =>
    run(() => (chat.shared_at ? api.chat.unshare(chat.id) : api.chat.share(chat.id)), "Could not update sharing.");

  const deleteChat = (chat: ChatRow) => {
    if (!window.confirm(`Delete "${chat.title || "Untitled chat"}"? This cannot be undone.`)) return;
    void run(() => api.chat.delete(chat.id), "Could not delete the chat.");
  };

  return (
    <div className="space-y-2">
      <ErrorBanner message={error} />
      <div className="rounded-md border bg-card">
        {chats.map((chat) => (
          <ProjectChatRow
            key={chat.id}
            chat={chat}
            projectId={projectId}
            isOwner={chat.user_id === user?.uid}
            renaming={renaming === chat.id}
            title={title}
            onTitleChange={setTitle}
            onRename={() => {
              setRenaming(chat.id);
              setTitle(chat.title ?? "");
            }}
            onSaveRename={() => void saveRename(chat.id)}
            onToggleShare={() => void toggleShare(chat)}
            onDelete={() => void deleteChat(chat)}
          />
        ))}
        {!chats.length ? <p className="p-6 text-sm text-muted-foreground">No chats in this matter yet.</p> : null}
      </div>
    </div>
  );
}

function ShareBadge({ shared }: { shared: boolean }) {
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", shared ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground")}>
      {shared ? "Shared" : "Private"}
    </span>
  );
}

function ProjectChatRow({
  chat,
  projectId,
  isOwner,
  renaming,
  title,
  onTitleChange,
  onRename,
  onSaveRename,
  onToggleShare,
  onDelete,
}: {
  chat: ChatRow;
  projectId: string;
  isOwner: boolean;
  renaming: boolean;
  title: string;
  onTitleChange: (value: string) => void;
  onRename: () => void;
  onSaveRename: () => void;
  onToggleShare: () => void;
  onDelete: () => void;
}) {
  const shared = Boolean(chat.shared_at);
  return (
    <div className="flex min-h-12 items-center gap-2 border-b px-3 last:border-b-0">
      {renaming ? (
        <Input value={title} autoFocus onChange={(event) => onTitleChange(event.target.value)} onBlur={onSaveRename} onKeyDown={(event) => event.key === "Enter" && onSaveRename()} className="h-8" />
      ) : (
        <Link to="/projects/$id/assistant/chat/$chatId" params={{ id: projectId, chatId: chat.id }} className="min-w-0 flex-1 truncate text-sm font-medium">{chat.title || "Untitled chat"}</Link>
      )}
      <ShareBadge shared={shared} />
      {/* Rename / share / delete are owner-only on the backend — only show them to the owner. */}
      {isOwner ? (
        <>
          <Button type="button" variant="ghost" size="sm" onClick={onToggleShare}>{shared ? "Make private" : "Share"}</Button>
          <Button type="button" variant="ghost" size="sm" onClick={onRename}>Rename</Button>
          <Button type="button" variant="ghost" size="icon" className="size-8" aria-label="Delete chat" onClick={onDelete}><Trash2 className="size-4" /></Button>
        </>
      ) : null}
    </div>
  );
}
